/**
 * Matching a CSV's columns to the fields we import them into.
 *
 * The importer used to require the file's header to equal our DISPLAY LABEL
 * character for character. A file exported from Osmos itself then failed on its
 * own columns: "Name" never matched the label "Item Name", "PriceA" never
 * matched "Price A", "ID" never matched "Id" — and those three are exactly what
 * "Modify Items" depends on. A user who did not spot the unmapped rows imported
 * a catalogue with no names or prices, or silently changed nothing.
 *
 * Kept as a plain function so it can be tested without standing up the dialog.
 */

import { CONTACT_HEADER_ALIASES_BY_LANGUAGE } from './csv-header-mapping.contact-aliases';

/** One destination field as the dialog holds it. */
export interface MappingField {
  /** display label, e.g. "Item Name" */
  name?: string;
  /** internal key, e.g. "name" | "priceA" | "id" */
  id?: string;
  /** the chosen CSV header; string, or array for multi-value fields */
  valueData?: string | string[] | null;
  /**
   * Mirrors valueData. The dialog sets both when it builds a field and the
   * multi-select control reads this one, so a resolver that updated only
   * valueData would leave the dropdown showing the old column.
   */
  options?: string | string[] | null;
  /** whether the import refuses to run without this field */
  star?: boolean;
  [k: string]: any;
}

/** The accent marks NFD splits off a letter ("í" -> "i" + U+0301). */
const COMBINING_ACCENTS = new RegExp('[\\u0300-\\u036f]', 'g');

/**
 * Punctuation, case and accents carry no meaning here: "Price A" === "PriceA" ===
 * "price_a", and "Categoría" === "Categoria". Accents are folded to their base
 * letter first (card #589) -- stripping them outright turned "Categoría" into
 * "categora", which matched nothing.
 */
export const normalizeHeader = (value: any): string =>
  String(value ?? '')
    .normalize('NFD')
    .replace(COMBINING_ACCENTS, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

/**
 * Extra spellings for the fields whose label and key BOTH differ from what
 * exports and hand-made files tend to call them.
 */
export const HEADER_ALIASES: Record<string, string[]> = {
  name: ['itemname', 'item', 'product', 'productname'],
  // priceA and pricesA are BOTH real field keys, not a typo: the header
  // endpoint returns one spelling or the other depending on the workspace, and
  // upload-csv.component.ts marks both as required in the same star check.
  // Whichever one arrives has to match a "Price A" column, so both carry the
  // same aliases.
  priceA: ['pricea', 'price', 'unitprice'],
  pricesA: ['pricea', 'price', 'unitprice'],
  id: ['itemid', 'code', 'reference'],
  category: ['categoryname'],
  subcategory: ['subcategoryname'],
  first_name: ['firstname', 'givenname'],
  last_name: ['lastname', 'surname', 'familyname'],
};

/**
 * Card #589: what an ITEM file written in another language calls each item
 * field, per language, keyed by the field's internal key (as item/headers/list
 * sends it). Used for item uploads only (resolveHeaderMapping's `items`
 * option): contacts and leads have their own fields. Written as people type
 * them -- normalizeHeader() folds case, accents and punctuation when matching.
 * The first entry of each is the label the item screens show in that language
 * (assets/i18n/<lang>.json COMMON.*). A bare "Descripción" is deliberately not
 * here: it could hold English or Spanish text, so the user picks.
 *
 * To add a language, add one block here; nothing else changes. One spelling
 * must never belong to two fields (the spec checks it), or a column would be
 * claimed by whichever field happens to be resolved first.
 */
export const HEADER_ALIASES_BY_LANGUAGE: Record<string, Record<string, string[]>> = {
  // Card #618: item-csv-template.ts's generated EN sample writes these 4 columns'
  // headers from en.json COMMON.* keys that collide with an EARLIER, unrelated
  // entry of the same key -- JSON.parse keeps the LAST one, which is the spelling
  // below, not the field's own server label ("Video Url" etc., still matched by
  // the own-label check). Only the 4 that actually differ need an entry here.
  en: {
    video_url: ['Link to Video'],
    website_url: ['Link to Website'],
    description: ['Description (English)'],
    'item_prices.discount': ['Discount'],
  },
  es: {
    name: ['Nombre del artículo', 'Nombre', 'Artículo', 'Producto', 'Nombre del producto'],
    category: ['Categoría'],
    subcategory: ['Subcategoría'],
    video_url: ['Enlace a Video', 'Video', 'URL del video'],
    website_url: ['Enlace a Sitio Web', 'Sitio Web', 'URL del sitio web'],
    code: ['Código del artículo', 'Código', 'Código del producto', 'SKU'],
    unit_quantity: ['Cantidad de unidades', 'Cantidad'],
    unit: ['Unidades', 'Unidad'],
    description: ['Descripción (English)', 'Descripción (inglés)'],
    description_es: ['Descripción (español)'],
    description_fr: ['Descripción (francés)'],
    item_coming_from: ['Tipo'],
    track_inventory: ['Inventario'],
    isDeactivated: ['Desactivado'],
    item_images: ['Imágenes'],
    'item_prices.discount': ['Descuento'],
    taxe_name: ['Nombre del Impuesto', 'Impuesto', 'Impuestos'],
    taxe_value: ['Valores de impuestos', 'Valor del impuesto'],
    comments: ['Comentarios'],
    'item_prices.fix_cost_per_unit': ['Costo Fijo por Unidad', 'Costo fijo'],
    'item_prices.sales_commission': ['Comisión de Ventas', 'Comisión'],
    'item_prices.min_price': ['Precio Mínimo'],
    // priceA and pricesA: both real keys, see HEADER_ALIASES above.
    priceA: ['Precio A', 'Precio'],
    pricesA: ['Precio A', 'Precio'],
    priceB: ['Precio B'],
    priceC: ['Precio C'],
    priceD: ['Precio D'],
  },
  // Card #599 (1 of 3 of #587's plan): French, next to Spanish.
  fr: {
    name: ["Nom de l'article", 'Nom', 'Article', 'Produit', 'Nom du produit'],
    category: ['Catégorie'],
    subcategory: ['Sous-catégorie'],
    video_url: ['Lien vers la vidéo', 'Vidéo', 'URL de la vidéo'],
    website_url: ['Lien vers le site web', 'Site Web', 'URL du site web'],
    code: ['Code article', 'Code produit', 'SKU'],
    unit_quantity: ['Quantité unitaire', 'Quantité'],
    unit: ['Unités', 'Unité'],
    description: ['Description (Anglais)'],
    description_es: ['Description (espagnol)'],
    description_fr: ['Description (français)'],
    item_coming_from: ['Type'],
    track_inventory: ["L'Inventaire", 'Inventaire'],
    isDeactivated: ['Désactivé'],
    item_images: ['Images'],
    'item_prices.discount': ['Remise'],
    taxe_name: ['Nom de la taxe', 'Taxe', 'Taxes'],
    taxe_value: ['Valeurs des taxes', 'Valeur de la taxe'],
    comments: ['Commentaires'],
    'item_prices.fix_cost_per_unit': ['Coût fixe par unité', 'Coût fixe'],
    'item_prices.sales_commission': ['Commission de vente', 'Commission'],
    'item_prices.min_price': ['Prix minimum'],
    // priceA and pricesA: both real keys, see HEADER_ALIASES above.
    priceA: ['Prix A', 'Prix'],
    pricesA: ['Prix A', 'Prix'],
    priceB: ['Prix B'],
    priceC: ['Prix C'],
    priceD: ['Prix D'],
  },
};

// Card #614: the contact alias table lives in its own file next to this one
// (csv-header-mapping.contact-aliases.ts) -- re-exported here so every
// existing import of it keeps working.
export { CONTACT_HEADER_ALIASES_BY_LANGUAGE };

/**
 * Every alternative spelling for one field: the English aliases, plus -- for
 * an item or a contact upload -- that upload's own language aliases. A
 * file's language is never guessed: a header in any supported language
 * matches, whatever language the screen is in.
 *
 * HEADER_ALIASES was written for items (card id/name/priceA/category...) before
 * the contacts upload existed, and 'id' and 'name' carry the SAME internal key
 * with a different meaning on a contact -- contact 'id' is the contact's own
 * id, not an item SKU, and contact 'name' is "Phone Name", not an item name.
 * So a contact upload (contacts: true) never gets THOSE two entries; it still
 * gets every other HEADER_ALIASES entry that also names a real contact field
 * (first_name/last_name: firstname, surname, ... are generic person-name
 * spellings, not item-only ones), plus its own CONTACT_HEADER_ALIASES_BY_LANGUAGE
 * table (card #706).
 *
 * @param fieldId  the field's internal key
 * @param items    true for the item upload (HEADER_ALIASES_BY_LANGUAGE)
 * @param contacts true for the contact upload (CONTACT_HEADER_ALIASES_BY_LANGUAGE);
 *                 ignored when items is also true, since a field is never both
 * @return the spellings, in no particular order; [] for an unknown key
 */
const ITEM_ONLY_HEADER_ALIAS_FIELDS = new Set(['id', 'name']);

export function headerAliasesFor(fieldId: string | undefined, items = false, contacts = false): string[] {
  if (!fieldId) return [];
  const table = items ? HEADER_ALIASES_BY_LANGUAGE : contacts ? CONTACT_HEADER_ALIASES_BY_LANGUAGE : null;
  const languageAliases = table
    ? Object.values(table).reduce<string[]>((all, byField) => all.concat(byField[fieldId] || []), [])
    : [];
  const englishAliases = contacts && ITEM_ONLY_HEADER_ALIAS_FIELDS.has(fieldId) ? [] : HEADER_ALIASES[fieldId] || [];
  return [...englishAliases, ...languageAliases];
}

/** Key prefix the field-list endpoints give an additional (custom) field: "additional_value_<field name>". */
export const ADDITIONAL_FIELD_KEY_PREFIX = 'additional_value_';

/** Suffix the field-list endpoints add to an additional field's label: "<field name> (Additional Field)". */
export const ADDITIONAL_FIELD_LABEL_SUFFIX = /\(\s*additional\s+field\s*\)$/i;

/**
 * The bare name of an additional field, which is exactly the column title the
 * catalog export writes for it.
 *
 * Card #535: the export titles an additional field's column with the field's
 * name ("Shippng Price"), but the dialog only knew the field by its label
 * ("Shippng Price (Additional Field)") and key ("additional_value_Shippng
 * Price"). Neither normalises to the column, so a re-uploaded export left every
 * additional field on "Select".
 *
 * @param field one destination field
 * @return the distinct names taken from the key and from the label (they
 *         normally agree), or [] for a standard field
 */
export function additionalFieldNames(field: MappingField): string[] {
  const names: string[] = [];
  const id = String(field?.id ?? '');
  if (id.startsWith(ADDITIONAL_FIELD_KEY_PREFIX)) {
    names.push(id.slice(ADDITIONAL_FIELD_KEY_PREFIX.length));
  }
  const label = String(field?.name ?? '').trim();
  if (ADDITIONAL_FIELD_LABEL_SUFFIX.test(label)) {
    names.push(label.replace(ADDITIONAL_FIELD_LABEL_SUFFIX, '').trim());
  }
  return [...new Set(names.filter((n) => n !== ''))];
}

/**
 * Resolve every field to a real column in the file.
 *
 * Tried in order: the value already chosen, the display label, the internal
 * key, the aliases, then an additional field's bare name. Anything still
 * unmatched is cleared — a field must not keep pointing at a label that is not
 * a column, which is how one could look unset on screen and still travel in
 * the payload.
 *
 * The bare-name fallback never gives an additional field a column another
 * field already resolved to (standard fields are resolved first; its label and
 * key carry "(Additional Field)" / "additional_value_" and so cannot collide). A custom field called
 * "Category" would otherwise silently import the Category column; left
 * unmapped, the user sees it and picks.
 *
 * @param fields  destination fields, mutated in place (the dialog binds to them)
 * @param headers the file's column names
 * @param options items: true for the item upload, which also matches the
 *                language aliases (HEADER_ALIASES_BY_LANGUAGE, card #589);
 *                contacts: true for the contact upload, which matches
 *                CONTACT_HEADER_ALIASES_BY_LANGUAGE instead (card #614)
 * @return the ids of fields left unmapped, for the caller to warn about
 */
export function resolveHeaderMapping(
  fields: MappingField[],
  headers: string[],
  options: { items?: boolean; contacts?: boolean } = {},
): string[] {
  const byNorm = new Map<string, string>();
  (headers || []).forEach((h) => {
    const k = normalizeHeader(h);
    if (k && !byNorm.has(k)) byNorm.set(k, h);
  });
  // Every field's own label and key, normalised: what an alias may not take from it.
  const exactOf = new Map<MappingField, Set<string>>();
  (fields || []).forEach((f) => exactOf.set(f, new Set([f.name, f.id].map(normalizeHeader).filter((c) => c))));
  const exactOfOthers = (field: MappingField): Set<string> => {
    const out = new Set<string>();
    exactOf.forEach((names, f) => { if (f !== field) names.forEach((n) => out.add(n)); });
    exactOf.get(field)?.forEach((n) => out.delete(n));
    return out;
  };

  const unmappedAt = new Set<number>();
  const claimed = new Set<string>();
  // Standard fields first, so they claim their columns before any additional
  // field. The sort is stable, and the result below is reported in the
  // caller's order, so only the resolution order changes.
  const ordered = (fields || [])
    .map((field, index) => ({ field, index, additional: additionalFieldNames(field).length > 0 }))
    .sort((a, b) => Number(a.additional) - Number(b.additional));
  ordered.forEach(({ field, index }) => {
    const wasArray = Array.isArray(field.valueData);
    const current = wasArray ? field.valueData[0] : field.valueData;

    // Resolve to the file's ACTUAL header text, never the label that happened
    // to normalise to it -- the dropdown compares by exact string, so keeping
    // "Price A" when the column is "PriceA" still shows as unselected.
    let resolved = current && byNorm.has(normalizeHeader(current))
      ? byNorm.get(normalizeHeader(current))
      : '';
    if (!resolved) {
      const own = [field.name, field.id].map(normalizeHeader);
      // An alias never takes a column that is ANOTHER field's own label or key
      // (card #589: more aliases, more chances to collide).
      const aliases = headerAliasesFor(field.id, !!options.items, !!options.contacts)
        .map(normalizeHeader)
        .filter((c) => !exactOfOthers(field).has(c));
      const hit = [...own, ...aliases].find((c) => c && byNorm.has(c));
      resolved = hit ? byNorm.get(hit) : '';
    }
    if (!resolved) {
      const bare = additionalFieldNames(field)
        .map(normalizeHeader)
        .find((c) => c && byNorm.has(c) && !claimed.has(byNorm.get(c) as string));
      resolved = bare ? byNorm.get(bare) : '';
    }
    if (resolved) claimed.add(resolved);

    field.valueData = wasArray ? (resolved ? [resolved] : []) : resolved;
    field.options = field.valueData;
    if (!resolved) unmappedAt.add(index);
  });

  return (fields || [])
    .filter((_, index) => unmappedAt.has(index))
    .map((field) => String(field.id ?? field.name ?? ''));
}

/** True when a mapping value names at least one column: a non-blank string, or an array holding one. */
export const namesAColumn = (value: unknown): boolean =>
  Array.isArray(value)
    ? value.some((v) => typeof v === 'string' && v.trim() !== '')
    : typeof value === 'string' && value.trim() !== '';

/**
 * True when a mapping value names at least one of the FILE's columns.
 *
 * Card #349: a non-blank value is not enough. headingType() seeds every field
 * with its own display LABEL ("First Name") when the field list arrives, and
 * that list can arrive after the file was read (csv/header/list took 3-35 s on
 * dev). The label then sat in the field, was not a column of the file, and
 * still counted as mapped -- so the "required columns" warning never appeared.
 */
export const namesAFileColumn = (value: unknown, headers: Set<string>): boolean => {
  const isHeader = (v: unknown) => typeof v === 'string' && v.trim() !== '' && headers.has(v.trim());
  return Array.isArray(value) ? value.some(isHeader) : isHeader(value);
};

/**
 * The ids of the fields with no column chosen RIGHT NOW.
 *
 * The answer resolveHeaderMapping() gives is only true at the moment the file
 * is read. Every column the user then picks by hand -- which is every column
 * whose header is not the English label, i.e. any file in Spanish or French --
 * left the warning naming fields that were already mapped (cards #299, #300).
 *
 * Field ids are compared, never labels, so the language of the file and of
 * the screen does not matter.
 *
 * @param fields    the dialog's fields, each holding its chosen column in valueData
 * @param committed once the dialog has left the mapping step, valueData holds
 *                  each column's DATA instead; pass the field-id -> column map
 *                  the upload sends (selectItem) and that is read instead
 * @param headers   the file's column headers. When given, a field counts as
 *                  mapped only if its value is one of them (card #349); when
 *                  omitted, any non-blank value counts, as before
 */
export function unmappedFieldIds(
  fields: MappingField[],
  committed?: Record<string, unknown> | null,
  headers?: string[] | null,
): string[] {
  const headerSet = headers ? new Set(headers.map((h) => String(h ?? '').trim())) : null;
  const isMapped = (value: unknown) =>
    headerSet ? namesAFileColumn(value, headerSet) : namesAColumn(value);
  return (fields || [])
    .filter((field) => !isMapped(committed ? committed[String(field?.id)] : field?.valueData))
    .map((field) => String(field?.id ?? field?.name ?? ''));
}
